import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { apiGet } from "../../processes/helpers/api";
import { studentSearchQuery } from "./teacherMocksHelpers";

export default function StudentPickerModal({ courseId, selectedIds, onConfirm, onClose }) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [results, setResults] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [pending, setPending] = useState([]);
  const inputRef = useRef(null);

  const confirmedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const pendingSet = useMemo(() => new Set(pending.map((s) => s.id)), [pending]);
  const totalPages = pagination?.totalPages ?? 1;

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    let isMounted = true;
    async function load() {
      try {
        setIsLoading(true);
        const qs = studentSearchQuery({ search: debouncedSearch, page, courseId });
        const response = await apiGet(`/api/v2/manager/users?${qs}`);
        if (!isMounted) return;
        setResults(response.body?.data || []);
        setPagination(response.body?.pagination || null);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    }
    load();
    return () => { isMounted = false; };
  }, [debouncedSearch, page, courseId]);

  function togglePending(student) {
    setPending((current) =>
      pendingSet.has(student.id)
        ? current.filter((s) => s.id !== student.id)
        : [...current, { id: student.id, name: student.name, email: student.email }]
    );
  }

  function handleConfirm() {
    onConfirm(pending);
    onClose();
  }

  return (
    <div
      className="teacher-mocks-picker-overlay"
      onMouseDown={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Adicionar aluno"
    >
      <div
        className="teacher-mocks-picker-modal"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="teacher-mocks-picker-header">
          <h3>Adicionar Aluno</h3>
          <button
            type="button"
            className="teacher-mocks-icon-button"
            onClick={onClose}
            aria-label="Fechar"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="teacher-mocks-picker-body">
          <div className="teacher-mocks-student-search-field">
            <Search size={14} className="teacher-mocks-student-search-icon" aria-hidden="true" />
            <input
              ref={inputRef}
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome ou e-mail..."
              className="teacher-mocks-student-search-input teacher-mocks-student-search-input--standalone"
              aria-label="Buscar alunos por nome ou e-mail"
              autoComplete="off"
            />
          </div>

          <div className="teacher-mocks-student-results teacher-mocks-student-results--scrollable" role="listbox" aria-label="Resultados da busca">
            {isLoading && (
              <div className="teacher-mocks-student-loading">Buscando...</div>
            )}

            {!isLoading && results.length === 0 && (
              <div className="teacher-mocks-student-empty">
                {debouncedSearch.trim() ? "Nenhum aluno encontrado." : "Digite para buscar alunos."}
              </div>
            )}

            {!isLoading && results.map((student) => {
              const confirmed = confirmedSet.has(student.id);
              const selected = pendingSet.has(student.id);
              return (
                <div
                  key={student.id}
                  className={`teacher-mocks-student-result-item${(confirmed || selected) ? " is-selected" : ""}`}
                  role="option"
                  aria-selected={confirmed || selected}
                >
                  <div className="teacher-mocks-student-info">
                    <span className="teacher-mocks-student-name">{student.name}</span>
                    <span className="teacher-mocks-student-email">{student.email}</span>
                  </div>
                  {confirmed ? (
                    <span className="teacher-mocks-student-confirmed-badge">Adicionado</span>
                  ) : (
                    <button
                      type="button"
                      className={`teacher-mocks-student-add-btn${selected ? " is-pending" : ""}`}
                      aria-label={selected ? `Remover ${student.name} da seleção` : `Selecionar ${student.name}`}
                      onClick={() => togglePending(student)}
                    >
                      {selected ? "Selecionado ✓" : "Selecionar"}
                    </button>
                  )}
                </div>
              );
            })}

            {!isLoading && totalPages > 1 && (
              <div className="teacher-mocks-student-pagination">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹</button>
                <span>{page} / {totalPages}</span>
                <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>›</button>
              </div>
            )}
          </div>
        </div>

        <div className="teacher-mocks-picker-footer">
          <button type="button" className="teacher-mocks-picker-cancel-btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="teacher-mocks-picker-confirm-btn"
            disabled={pending.length === 0}
            onClick={handleConfirm}
          >
            Confirmar{pending.length > 0 ? ` (${pending.length})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
